import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import * as XLSX from "xlsx";
import toast from 'react-hot-toast';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,  
    CircularProgress, 
    Tooltip, 
    Table, 
    TableHead, 
    TableRow, 
    TableCell, 
    TableBody,
    Paper, 
    Card, 
    CardContent, 
    ListItemText,
    FormControl, 
    InputLabel, 
    MenuItem, 
    Select,
    Checkbox, 
    FormGroup, 
    FormControlLabel,
} from '@mui/material';
import { 
    FileUpload,
    SaveAlt,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { hasPermission } from 'state/checkPermission';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import ConfirmDialog from 'components/ConfirmDialog';


const DataImporter = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user);     
    const theme = useTheme();   
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const [collections, setCollections] = useState([]);
    const [collection, setCollection] = useState("");
    const [fields, setFields] = useState([]);
    const [selectedFields, setSelectedFields] = useState([]);
    const [keys, setKeys] = useState([]);
    const [data, setData] = useState([]);
    const [fileName, setFileName] = useState("");
    const [importMode, setImportMode] = useState("update");


    // Load collections on mount
    useEffect(() => {       
        api.get('/dataimporter/collections')
           .then((res) => setCollections(res.data))
           .catch((err) => toast.error("Failed to load collections", err));
    }, []);

    // Load fields when collection changes
    useEffect(() => {
        if (collection) {            
            setLoading(true);
            const url = `/dataimporter/collections/${collection}/fields`;
            api.get(url)
               .then((res) => setFields(res.data))
               .catch((err) => toast.error(`Failed to load fields: ${err.message}`))
               .finally(() => setLoading(false));
        } else {
            setFields([]);
        }
    }, [collection]);
  
    const handleFileUpload = useCallback((e) => {      
        setError(null);  
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                let jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" }); // fills empty cells
                
                // Lọc bỏ cột trống
                if (jsonData.length > 0) {
                    const firstRowKeys = Object.keys(jsonData[0]);
                    const validKeys = firstRowKeys.filter((key) =>
                        key && jsonData.some((row) => row[key] !== "")
                    );

                    jsonData = jsonData.map((row) => {
                        const filtered = {};
                        validKeys.forEach((k) => (filtered[k] = row[k]));
                        return filtered;
                    });
                }
                setData(jsonData);
            } catch (err) {
                toast.error("Failed to parse file!");
            }
        };
        reader.onerror = () => toast.error("Error reading file!");
        reader.readAsArrayBuffer(file);
    }, []);

    // Toggle fields
    const toggleField = (field) => {
        setSelectedFields((prev) =>
            prev.includes(field)
                ? prev.filter((f) => f !== field)
                : [...prev, field]
            );
    };
   
    const handleImport = useCallback(async () => {
        if (!collection) return toast.error("Please select a collection!");
        if (data.length === 0) return toast.error("No data loaded!");
        if (importMode !== "insert" && keys.length === 0) return toast.error("Select at least one key field!");
        if (importMode !== "insert" && selectedFields.length === 0) return toast.error("Select at least one field!");

        try {
            setError(null);
            setLoading(true);

            const url = `/dataimporter/import/${collection}`;
            const payload = { data, selectedFields, keys, importMode };
            
            const res = await api.post(url, payload);

            toast.success(`Import complete!\nMode: ${importMode}\nUpdated: ${res.data.updatedCount}\nInserted: ${res.data.insertedCount}\nSkipped: ${res.data.skippedCount}`);            
        } catch (error) {
            setError(error);
            toast.error(error.response?.data?.message || "Failed to fetch data!");
        } finally {
            setLoading(false);
            setConfirmDialogOpen(false);
        }
    }, [
        data, 
        selectedFields, 
        keys, 
        collection, 
        importMode
    ]);

    
    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header 
                    title="Data Importer" 
                    subtitle={`${loading ? 'Loading...' : 'List of Collections'}`}
                /> 
                {!loading && (<Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Upload" arrow>
                        <span>
                            <label htmlFor={"upload-file"}>
                                <StyledButton 
                                    component="span" 
                                >
                                    <FileUpload />
                                </StyledButton>
                            </label>
                        </span>
                    </Tooltip>
                    <input
                        id="upload-file"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                    /> 

                    <FormControl size="small" sx={{ minWidth: isMobile ? 80 : 200 }}>
                        <InputLabel>Collection</InputLabel>
                        <Select
                            label="Collection"
                            value={collection}
                            onChange={(e) => {
                                setError(null);
                                setSelectedFields([]);
                                setKeys([]);
                                setCollection(e.target.value);
                            }}
                        >
                            {collections.map((col) => (
                                <MenuItem key={col} value={col}>
                                    {col}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: isMobile ? 80 : 200 }}>
                        <InputLabel>Keys</InputLabel>
                        <Select
                            multiple
                            label="Keys"
                            value={keys}
                            onChange={(e) => {
                                setError(null);
                                setKeys(e.target.value);
                            }}
                            renderValue={(selected) =>
                                selected.length === 0 ? "None" : selected.join(", ")
                            }
                        >
                            {selectedFields.map((field) => (
                                <MenuItem key={field} value={field}>
                                    <Checkbox checked={keys.indexOf(field) > -1} />
                                    <ListItemText primary={field} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {!isMobile && (
                        <FormControl size="small" sx={{ minWidth: isMobile ? 60 : 100 }}>
                            <InputLabel>Import Mode</InputLabel>
                            <Select
                                label="Import Mode"
                                value={importMode}
                                onChange={(e) => setImportMode(e.target.value)}
                            >
                                <MenuItem value="update">Update</MenuItem>
                                <MenuItem value="insert">Insert</MenuItem>
                                <MenuItem value="upsert">Upsert</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                                            
                    <Tooltip title="Import" arrow>
                        <span>
                            <StyledButton 
                                disabled={loading || !data.length}
                                onClick={() => {
                                    if (!hasPermission(user?.permissions, "DataImporter", "import")) return;
                                    setConfirmDialogOpen(true);
                                }}
                            >
                                <SaveAlt />
                            </StyledButton>
                        </span>            
                    </Tooltip>
                </Box>)}                
            </FlexBetween>            
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                    sx={{
                        "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
                    }}
                >
                    <Box
                        height={LAYOUT_HEIGHT}  
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
                    >                            
                        <Card elevation={3}>
                            <CardContent>                                
                                {/* File Info */}
                                {fileName && (
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        📁 File: <strong>{fileName}</strong> ({data.length} rows)
                                    </Typography>
                                )}

                                {/* Key Selection */}
                                {loading ? (
                                    <CircularProgress />
                                ) : (
                                    fields.length > 0 && (
                                        <Box mb={1}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                🔑 Select key fields (for identifying existing records):
                                            </Typography>
                                            <FormGroup row>
                                                {fields?.map((field) => (
                                                    <FormControlLabel
                                                        key={field}
                                                        control={
                                                            <Checkbox
                                                                checked={selectedFields.includes(field)}
                                                                onChange={() => toggleField(field)}
                                                            />
                                                        }
                                                        label={field}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Box>
                                    )
                                )}

                                {/* Preview Table */}
                                {data.length > 0 && (
                                    <Paper sx={{ overflow: "auto", maxHeight: '66vh' }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    {Object.keys(data[0]).map((key) => (
                                                        <TableCell key={key}>{key}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data?.map((row, i) => (
                                                    <TableRow key={i}>
                                                        {Object.values(row).map((val, j) => (
                                                            <TableCell key={j}>{val?.toString()}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        {data.length > 0 && (
                                            <Typography variant="body2" color="text.secondary" p={1} mt={1}>
                                                Showing {data.length} rows
                                            </Typography>
                                        )}
                                    </Paper>
                                )}
                            </CardContent>
                        </Card>
                    </Box>  
                </Box>
            )}

            <ConfirmDialog
              open={confirmDialogOpen}
              onClose={() => setConfirmDialogOpen(false)}
              onConfirm={handleImport}
            />
        </Box>
    );
};

export default DataImporter;
